import { Component, Input, OnDestroy, forwardRef } from "@angular/core";
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALUE_ACCESSOR
} from "@angular/forms";
import { first, takeUntil } from "rxjs/operators";

import { BaseJsonEditorComponent } from "./base-json-editor.component";
import { CollapseAllService } from "./collapse-all.service";
import { treeAnimations } from "./json-tree-animations";

@Component({
  selector: "ta-json-object-editor",
  templateUrl: "./json-object-editor.template.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonObjectEditorComponent),
      multi: true
    }
  ],
  animations: treeAnimations
})
export class JsonObjectEditorComponent extends BaseJsonEditorComponent
  implements ControlValueAccessor, OnDestroy {
  @Input()
  animate: boolean = false;

  formGroup = this.fb.group({});

  get controlNames(): string[] {
    return Object.keys(this.formGroup.controls);
  }

  constructor(private fb: FormBuilder, collapseAllService: CollapseAllService) {
    super(collapseAllService);
  }

  removeControl(name: string): void {
    this.formGroup.removeControl(name);
  }

  submitNewControlForm(child: any): void {
    this.formGroup.addControl(
      child.key,
      this.fb.control(this.getNewChildValue(child))
    );
    this.showNewControlForm = false;
  }

  writeValue(newValue: object): void {
    this.removeControlsNotIn(newValue);
    this.upsertControls(newValue);
    this.formGroup.patchValue(newValue);
  }

  registerOnChange(fn: any): void {
    this.formGroup.valueChanges
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(fn);
  }

  registerOnTouched(touchCb: any): void {
    this.formGroup.valueChanges
      .pipe(first(), takeUntil(this.onDestroyEmitter))
      .subscribe(touchCb);
  }

  private removeControlsNotIn(value: object): void {
    const newControlNames = Object.keys(value);
    for (let controlName of this.controlNames) {
      if (!newControlNames.includes(controlName)) {
        this.formGroup.removeControl(controlName);
      }
    }
  }

  private upsertControls(schema: object): void {
    for (let [key, value] of Object.entries(schema)) {
      if (this.controlNames.includes(key)) {
        this.formGroup.patchValue({ [key]: value });
      } else {
        this.formGroup.addControl(key, this.fb.control(value));
      }
    }
  }
}
